import type { InspectionDraft } from '../stores/checkin.store'
import type { ConditionGrade, InspectionPhoto } from '../types/inspection.types'

export interface HydratedInspectionDraft {
  fuelLevel: number
  fuelTouched: boolean
  odometerText: string
  exteriorCondition: ConditionGrade | undefined
  interiorCondition: ConditionGrade | undefined
  notes: string
  photos: InspectionPhoto[]
  guestSignature: string | null
  hostSignature: string | null
}

/**
 * A route-param change can reuse the same React screen instance. Returning a
 * complete empty shape for a destination without a draft matters just as much
 * as restoring a real one: otherwise the previous rental's odometer, photos
 * and signatures remain visible for one render and can be persisted under the
 * new rental id.
 */
export function hydrateInspectionDraft(draft: InspectionDraft | null): HydratedInspectionDraft {
  return {
    fuelLevel: draft?.fuelLevel ?? 50,
    fuelTouched: draft?.fuelTouched ?? false,
    odometerText: draft?.odometerText ?? '',
    exteriorCondition: draft?.exteriorCondition,
    interiorCondition: draft?.interiorCondition,
    notes: draft?.notes ?? '',
    photos: draft?.photos ?? [],
    guestSignature: draft?.guestSignature ?? null,
    hostSignature: draft?.hostSignature ?? null,
  }
}

/**
 * Saving is legal only after this exact rental was hydrated. React runs the
 * hydrate effect and the persist effect from the old render in the same flush;
 * comparing IDs prevents that old render from copying evidence across rentals.
 */
export function canPersistInspectionDraft(
  rentalId: string | undefined,
  hydratedRentalId: string | null,
): rentalId is string {
  return Boolean(rentalId && rentalId === hydratedRentalId)
}
